/**
 * LWPOLYLINE entity → `RenderableEntity` adapter — TYPED variant (DEFUSE D3).
 *
 * Counterpart of the legacy `lwpolylineToRenderable.ts` adapter that reads the
 * `EntityPreview.properties` string bag ("Vertices" / "Closed"). This typed
 * adapter reads the canonical `LwpolylineEntity` fields off the `DrawingEntity`
 * model directly (`vertices` — each a `polylineVertexSchema` carrying
 * `x` / `y` / `bulge` / `startWidth` / `endWidth`, plus `closed` and
 * `elevation`), then calls the SAME shared `buildLwpolylineSceneGeometry`
 * builder so the produced `SceneGeometry` + bounds match the legacy path.
 *
 * DOCUMENTED SUPERSET — the typed path is MORE faithful than the legacy bag:
 * the producer `dxfGeometry.buildLwpolylinePreview` emits ONLY the `x,y` of each
 * vertex into the `Vertices` string and writes NO `Bulges`, `Start widths`,
 * `End widths`, or `Elevation` keys. So the legacy `lwpolylineToRenderable`,
 * fed a `buildShellEntityPreview` bag, always reads bulges = [], widths = [],
 * elevation = 0. This typed adapter reads the REAL `bulge` / `startWidth` /
 * `endWidth` / `elevation` off the model, so an arc-bulged or variable-width
 * polyline surfaces its true geometry here while still being byte-identical to
 * legacy for the apples-to-apples case (no bulges / uniform width / elevation
 * 0). The parity test asserts both the apples-to-apples equality AND the
 * documented superset.
 *
 * z handling: `polylineVertexSchema` is 2D (x / y only). The entity's
 * `elevation` (DXF group 38) is the common z of every vertex, so this adapter
 * feeds `z = elevation` to each raw vertex before the offset — matching what the
 * legacy adapter does when an "Elevation" key IS present, and producing z = 0
 * (the legacy bag baseline) when elevation defaults to 0.
 *
 * Width omission: `buildLwpolylineSceneGeometry` drops the `widths` field when
 * the list is empty. This adapter builds the per-vertex `widths` list ONLY
 * when some segment is actually WIDE — either the per-vertex `startWidth` /
 * `endWidth` (groups 40/41) or, when NO vertex declares one, the entity-level
 * CONSTANT width (group 43); the two are mutually exclusive per entity, which
 * is `drawing-model`'s `polylineWidth.ts`'s rule, not this adapter's. A polyline that is wide nowhere gets no `widths` field, so
 * its geometry shape stays exactly the `{kind, points, closed}` the bag
 * baseline produces. `batchedLayer.ts` reads the field and draws the BAND
 * (`adapters/geom/polylineWidthOutline.ts`); a zero-width polyline is the
 * plain centreline governed by lineweight, as before.
 *
 * LWPOLYLINE is in `TYPED_RENDER_KINDS` (`./index.ts`), so this adapter — not
 * the legacy bag `lwpolylineToRenderable` — is what the live render, pick and
 * bulk-load lifts call (`../typedFirstRenderable.ts`). The legacy twin
 * survives only for the in-flight draft-preview channel.
 */

import {
  hasPolylineWidth,
  lwpolylineWidths,
  type LwpolylineEntity,
} from "@dxf-ai-editor/drawing-model";

import type { RenderableEntity, Vec3 } from "../../types";

import {
  buildRenderableShell,
  type AdapterContext,
} from "../adapterContext";
import { buildLwpolylineSceneGeometry } from "../geom/lwpolylineGeometry";

/** Typed adapter for the `LWPOLYLINE` entity (`lwpolylineEntitySchema`). */
export function lwpolylineFromEntity(
  entity: LwpolylineEntity,
  ctx: AdapterContext,
): RenderableEntity {
  // `polylineVertexSchema` is 2D; the entity-level `elevation` (DXF group 38)
  // is the common z of every vertex. Match the legacy "Elevation" bag read
  // (absent → 0).
  const elevation = entity.elevation ?? 0;
  const z = Number.isFinite(elevation) ? elevation : 0;
  const verticesRaw: Vec3[] = entity.vertices.map((v) => ({
    x: v.x,
    y: v.y,
    z,
  }));

  // One bulge per *segment*. The model stores a bulge per vertex (closing-
  // segment bulge on the last vertex for a closed polyline). Surface the real
  // bulges; treat an all-zero bulge list as "no arcs" so the geometry shape
  // matches the legacy (bulge-less) bag baseline.
  const bulgesAll = entity.vertices.map((v) => v.bulge ?? 0);
  const bulges = bulgesAll.some((b) => b !== 0) ? bulgesAll : [];

  // Per-vertex start/end widths (DXF group codes 40 / 41) OR the entity-level
  // CONSTANT width (group 43) — whichever channel the entity is in, which is
  // `drawing-model`'s `polylineWidth.ts`, the ONE place that rule is written,
  // shared with the heavy-POLYLINE adapter, the vector-PDF band and anything
  // later. (It matters far more than it looks:
  // PLINE ▸ Width over a whole run writes 43 and no 40/41 at all, so reading
  // only 40/41 left every uniform-width polyline at zero and the batcher drew
  // a hairline centreline for a band the properties panel reported as wide.)
  // Emit the list ONLY when some segment is actually wide. An all-zero list is
  // a zero-width polyline → no `widths` field, matching the legacy bag
  // baseline (which never emits per-vertex widths).
  const widthsAll = lwpolylineWidths(entity);
  const widths = hasPolylineWidth(widthsAll) ? widthsAll : [];

  const { geometry, bounds } = buildLwpolylineSceneGeometry(
    verticesRaw,
    entity.closed,
    bulges,
    widths,
  );

  return buildRenderableShell(entity, ctx, geometry, bounds);
}
